import { isFromIframe } from './post-message';
import type { ParentInboundMessage } from '../../protocol';

type Handler = (message: ParentInboundMessage) => void;

interface Registration {
  origin: string;
  handler: Handler;
}

/**
 * D1 — module-singleton-shaped message router with ONE `window` `message`
 * listener, refcounted (attached on first `register`, removed on last
 * `unregister`). Every mounted `CardField` shares the same
 * `secureFieldsOrigin`, so origin alone cannot disambiguate between
 * multiple iframes on one page — `event.source === iframe.contentWindow`
 * identity is the only reliable discriminator (design D1).
 *
 * Threat-matrix boundary: a message is delivered to a handler only if its
 * `event.source` matches a REGISTERED source AND `event.origin` matches
 * that exact registration's expected origin AND the payload passes
 * `isFromIframe`'s shape guard. Any mismatch drops the message silently.
 */
export class MessageRouter {
  private readonly registrations = new Map<MessageEventSource, Registration>();
  private windowListener: ((event: MessageEvent) => void) | undefined;

  register(origin: string, source: MessageEventSource, handler: Handler): void {
    this.registrations.set(source, { origin, handler });
    if (!this.windowListener) {
      this.windowListener = (event: MessageEvent) => this.handleMessage(event);
      window.addEventListener('message', this.windowListener);
    }
  }

  unregister(source: MessageEventSource): void {
    this.registrations.delete(source);
    if (this.registrations.size === 0 && this.windowListener) {
      window.removeEventListener('message', this.windowListener);
      this.windowListener = undefined;
    }
  }

  private handleMessage(event: MessageEvent): void {
    if (!event.source) return;
    const registration = this.registrations.get(event.source);
    if (!registration) return;
    if (event.origin !== registration.origin) return;
    if (!isFromIframe(event.data)) return;
    registration.handler(event.data);
  }
}

/** D1 — the one process-wide router instance production code shares.
 * Tests construct their own isolated `MessageRouter()` instead. */
export const sharedMessageRouter = new MessageRouter();
